'use client'

import type { ReactNode } from 'react'
import { useRef } from 'react'
import { motion, useMotionTemplate, useReducedMotion, useScroll, useTransform } from 'motion/react'

import cn from '@config/tailwind/cn'

interface StoryPanelProps {
  /** The panel colour, e.g. `bg-neutral-black`. */
  bgClassName: string
  className?: string
  children: ReactNode
}

/** Inset and corner radius of the resting panel: 12px / 28px on phones, a 1360px card from `md`. */
const PANEL_SHAPE = '[--gutter:12px] [--radius:28px] md:[--gutter:max(24px,calc((100%_-_1360px)/2))] md:[--radius:40px]'

/**
 * A rounded, inset colour panel that grows to the full page width as it reaches the top of the
 * screen, and shrinks back once its content has scrolled past. The colour is a clip-path on a
 * background layer, so the growth repaints but never re-lays out the content. The content column
 * carries the colour too, so a ScrollStory inside can inherit it for mix-blend-lighten.
 */
export function StoryPanel({ bgClassName, className, children }: StoryPanelProps) {
  const ref = useRef<HTMLElement>(null)
  const reduceMotion = useReducedMotion()
  const { scrollYProgress: enter } = useScroll({ target: ref, offset: ['start 0.7', 'start start'] })
  const { scrollYProgress: leave } = useScroll({ target: ref, offset: ['end end', 'end 0.3'] })
  // 1 = resting card, 0 = full width
  const inset = useTransform(() => 1 - Math.min(enter.get(), 1 - leave.get()))
  const clipPath = useMotionTemplate`inset(0 calc(var(--gutter) * ${inset}) round calc(var(--radius) * ${inset}))`

  return (
    <section ref={ref} className={cn('relative isolate px-4 py-20 md:px-8', className)}>
      <motion.div
        aria-hidden
        className={cn('absolute inset-0 -z-10', PANEL_SHAPE, bgClassName)}
        style={{ clipPath: reduceMotion ? 'inset(0 var(--gutter) round var(--radius))' : clipPath }}
      />
      <div className={cn('mx-auto max-w-280', bgClassName)}>{children}</div>
    </section>
  )
}
